import { Wallet, Building, Receipt, TrendingUp, TrendingDown } from 'lucide-react';
import { usePaymentSummary } from '../../hooks/usePayments';
import { formatNumber } from '@shared/utils/format';

export default function PaymentSummaryCards({ ym }: { ym: string }) {
    const { data, isLoading } = usePaymentSummary(ym);

    if (isLoading) {
        return (
            <div className="grid grid-cols-4 gap-4 mb-5">
                {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="bg-white rounded shadow-sm border border-[#E6E9ED] p-4 animate-pulse"><div className="h-14" /></div>
                ))}
            </div>
        );
    }

    const s = data;
    const up = (s?.diffAmount ?? 0) >= 0;
    const cardBase = 'bg-white rounded shadow-sm border border-[#E6E9ED] p-4';

    return (
        <div className="grid grid-cols-4 gap-4 mb-5">
            <div className={cardBase}>
                <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-[#1ABB9C]/10 flex items-center justify-center">
                        <Wallet className="w-6 h-6 text-[#1ABB9C]" />
                    </div>
                    <div>
                        <p className="text-[11px] text-[#ADB5BD] uppercase font-medium">총 결제액</p>
                        <p className="text-xl font-bold text-[#73879C]">{formatNumber(s?.totalAmount ?? 0)}원</p>
                    </div>
                </div>
                <div className="mt-2 pt-2 border-t border-[#E6E9ED] text-[11px]">
                    {up ? <TrendingUp className="w-3 h-3 inline text-[#1ABB9C]" /> : <TrendingDown className="w-3 h-3 inline text-[#E74C3C]" />}
                    <span className={up ? 'text-[#1ABB9C] ml-1' : 'text-[#E74C3C] ml-1'}>
                        {up ? '+' : ''}{formatNumber(s?.diffAmount ?? 0)}원 ({s?.diffRate ?? 0}%)
                    </span>
                    <span className="text-[#ADB5BD]"> 전월 대비</span>
                </div>
                <div className="mt-1 text-[11px] text-[#ADB5BD]">
                    일반 <span className="text-[#73879C] font-medium">{formatNumber(s?.payAmount ?? 0)}</span>
                    {' · '}포인트 <span className="text-[#73879C] font-medium">{formatNumber(s?.pointAmount ?? 0)}</span>
                </div>
                {s?.isCurrentMonth && (
                    <div className="mt-1 text-[10px] text-[#E67E22]">
                        진행 중{s?.lastUpdated ? ` · ${new Date(s.lastUpdated).toLocaleString('ko-KR', { month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })} 기준` : ''}
                    </div>
                )}
            </div>

            <div className={cardBase}>
                <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-[#3498DB]/10 flex items-center justify-center">
                        <Building className="w-6 h-6 text-[#3498DB]" />
                    </div>
                    <div>
                        <p className="text-[11px] text-[#ADB5BD] uppercase font-medium">결제 단지</p>
                        <p className="text-xl font-bold text-[#73879C]">{formatNumber(s?.townCount ?? 0)}</p>
                    </div>
                </div>
                <div className="mt-2 pt-2 border-t border-[#E6E9ED] text-[11px] text-[#ADB5BD]">결제 발생 단지 수</div>
            </div>

            <div className={cardBase}>
                <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-[#9B59B6]/10 flex items-center justify-center">
                        <Receipt className="w-6 h-6 text-[#9B59B6]" />
                    </div>
                    <div>
                        <p className="text-[11px] text-[#ADB5BD] uppercase font-medium">결제 건수</p>
                        <p className="text-xl font-bold text-[#73879C]">{formatNumber(s?.billCount ?? 0)}</p>
                    </div>
                </div>
                <div className="mt-2 pt-2 border-t border-[#E6E9ED] text-[11px] text-[#ADB5BD]">type=0 정상 결제</div>
            </div>

            <div className={cardBase}>
                <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-[#E67E22]/10 flex items-center justify-center">
                        <Wallet className="w-6 h-6 text-[#E67E22]" />
                    </div>
                    <div>
                        <p className="text-[11px] text-[#ADB5BD] uppercase font-medium">세대당 평균</p>
                        <p className="text-xl font-bold text-[#73879C]">{formatNumber(s?.avgPerHousehold ?? 0)}원</p>
                    </div>
                </div>
                <div className="mt-2 pt-2 border-t border-[#E6E9ED] text-[11px] text-[#ADB5BD]">사용 세대 기준</div>
            </div>
        </div>
    );
}
